import moment from 'moment';
import { useCallback, useEffect } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import type { VariablesOf } from '@graphql-typed-document-node/core';
import useAppSelector from '../app/redux/useAppSelector';
import { ACTION_POST_READ, CATEGORY_PRODUCT } from '../constants/analytics';
import { gql } from '../graphql/generated/gql';
import { track } from '../utils/analytics';

const INACTIVITY_TIMEOUT = 30000;
const MIN_ACTIVITY_READ = 10000;
const MIN_ANALYTICS_READ = 30000;

const READ_POST_MUTATION = gql(/** @complexity 2 */ `
  mutation readPost(
    $postId: ID!
    $time: Int!
    $queryId: String
    $resultId: String
  ) {
    readPost(id: $postId, time: $time, queryId: $queryId, resultId: $resultId) {
      id
    }
  }
`);

export const useReadPost = () => {
  const [readPost] = useMutation(READ_POST_MUTATION);
  return useCallback(
    (variables: VariablesOf<typeof READ_POST_MUTATION>) =>
      readPost({ variables }),
    [readPost],
  );
};

const usePostActivityTracker = (
  postId: string | null,
  queryId?: string,
  resultId?: string,
) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const client = useApolloClient();
  const readPost = useReadPost();

  useEffect(() => {
    if (!postId) {
      return undefined;
    }
    let readStart: number | null = null;
    let lastActive: number | null = null;
    let inactiveInterval: NodeJS.Timeout | null = null;

    const sendRead = (endTime: number) => {
      if (readStart === null) return;
      const time = endTime - readStart;
      if (time > MIN_ACTIVITY_READ) {
        readPost({ postId, time, queryId, resultId });
      }
      if (time > MIN_ANALYTICS_READ) {
        track(ACTION_POST_READ, {
          category: CATEGORY_PRODUCT,
          label: postId,
          value: time,
        });
      }
    };

    const onInactive = () => {
      const now = moment().valueOf();
      if (lastActive !== null && now - lastActive > INACTIVITY_TIMEOUT * 1.2) {
        // Wake detected
        sendRead(lastActive);
      } else {
        sendRead(now);
      }
      if (inactiveInterval) {
        clearInterval(inactiveInterval);
      }
      inactiveInterval = null;
      readStart = null;
    };

    const onActive = () => {
      readStart = moment().valueOf();
      lastActive = moment().valueOf();
      inactiveInterval = setInterval(() => {
        if (moment().valueOf() - lastActive! > INACTIVITY_TIMEOUT) {
          onInactive();
        }
      }, INACTIVITY_TIMEOUT);
    };

    const onActivity = () => {
      if (readStart === null) {
        onActive();
      }
      const now = moment().valueOf();
      if (lastActive !== null && now - lastActive > INACTIVITY_TIMEOUT * 1.2) {
        // Wake detected
        sendRead(lastActive);
        readStart = now;
      }
      lastActive = now;
    };

    onActive();

    document.addEventListener('mousemove', onActivity);
    document.addEventListener('touchstart', onActivity);

    return () => {
      onInactive();
      if (inactiveInterval) {
        clearInterval(inactiveInterval);
      }
      document.removeEventListener('mousemove', onActivity);
      document.removeEventListener('touchstart', onActivity);
    };
  }, [postId, queryId, resultId, readPost, client, currentUser]);
};

interface PostActivityTrackerProps {
  postId: string | null;
  queryId?: string;
  resultId?: string;
}

const PostActivityTracker = ({
  postId,
  queryId,
  resultId,
}: PostActivityTrackerProps) => {
  usePostActivityTracker(postId, queryId, resultId);

  return null;
};

export default PostActivityTracker;
